import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Loader, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import type { RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';
import {
  formatAmount,
  formatDay,
  formatInt,
  type RequestViewItem,
  type RequestViewRow,
  type RequestsViewResponse
} from '@parts/shared';
import { errorText, getJson } from '../../api';
import { PageHeader } from '../../components/layout/PageHeader';
import { TableGrid, type ColumnDefinition } from '../../components/table/TableGrid';
import { badge, esc, moneyCell, numberCell, person } from '../../components/table/cells';
import { t, tn, useI18n } from '../../i18n';
import { can } from '@parts/shared';
import { IconCollapse, IconDownload, IconExpand, IconSearch, IconSplit, IconX } from '../../icons';
import { useRole } from '../../role';
import { ExportDialog } from './ExportDialog';
import { SplitDialog } from './SplitDialog';
import { getPref, setPref } from '../../settings';
import { useAppState } from '../../state';
import classes from './RequestsView.module.css';

interface TreeRow extends Record<string, unknown> {
  kind: 'req' | 'item';
  key: string;
  tree: string;
  _children?: TreeRow[];
}

type Filter = 'all' | 'work' | 'overdue' | 'done' | 'cancelled';

/** Названия фильтров — ключи перевода. */
const FILTERS: { id: Filter; title: string; match: (r: RequestViewRow) => boolean }[] = [
  { id: 'all', title: 'Все', match: () => true },
  { id: 'work', title: 'В работе', match: (r) => r.status === 'Открыт' || r.status === 'Частично выполнен' || r.status === 'Просрочен' },
  { id: 'overdue', title: 'Просрочены', match: (r) => r.status === 'Просрочен' },
  { id: 'done', title: 'Выполнены', match: (r) => r.status === 'Выполнен' },
  { id: 'cancelled', title: 'Отменены', match: (r) => r.status === 'Отменено' }
];

/** Ключи настроек пользователя. */
const PREF_COLLAPSED = 'requests:collapsed';
const PREF_FILTER = 'requests:filter';

const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatAmount(v));

function itemRow(req: RequestViewRow, it: RequestViewItem): TreeRow {
  return {
    kind: 'item',
    key: `${req.no}:${it.art}`,
    tree: it.art,
    suggest: it.suggestion,
    name: it.name,
    qty: it.cancelled ? 0 : it.qty,
    price: money(it.price),
    amount: money(it.amount),
    fprice: money(it.factoryPrice),
    famount: money(it.factoryAmount),
    margin: money(it.margin),
    marginValue: it.margin,
    bought: it.bought,
    boughtAmount: money(it.boughtAmount),
    tobuy: it.toBuy,
    stock: it.stock ?? '',
    stockDate: it.stockDate ? formatDay(it.stockDate) : '',
    surplus: it.surplus || '',
    status: it.status,
    author: '',
    note: it.note,
    cancelled: it.cancelled,
    overdue: it.status === 'Просрочен'
  };
}

function requestRow(req: RequestViewRow, items: RequestViewItem[] = req.items): TreeRow {
  const positions = tn(req.positions, ['позиция', 'позиции', 'позиций']);
  const removed = req.cancelled ? `, ${t('снято {n}', { n: req.cancelled })}` : '';
  return {
    kind: 'req',
    key: req.no,
    tree: req.no,
    suggest: '',
    name: `${t('от {date}', { date: formatDay(req.created) })}, ${positions}${removed}`,
    qty: req.qty,
    price: '',
    amount: money(req.amount),
    fprice: '',
    famount: money(req.factoryAmount),
    margin: money(req.margin),
    marginValue: req.margin,
    bought: req.bought,
    boughtAmount: money(req.boughtAmount),
    tobuy: Math.max(0, req.qty - req.bought),
    stock: '',
    stockDate: '',
    surplus: req.surplus || '',
    status: req.status,
    author: req.author,
    note: req.comment,
    cancelled: false,
    overdue: req.status === 'Просрочен',
    _children: items.map((it) => itemRow(req, it))
  };
}

/** Экран «Текущие запросы»: запрос и его позиции, как лист «Запросы» в таблице. */
export function RequestsView() {
  const { lang, tx } = useI18n();
  const { state } = useAppState();
  const [rows, setRows] = useState<RequestViewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>(() => getPref<Filter>(PREF_FILTER, 'all'));
  const table = useRef<Tabulator | null>(null);
  const role = useRole();
  const [exportOpen, setExportOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);

  // Свёрнутые запросы запоминаются для пользователя; по умолчанию всё развёрнуто
  const collapsed = useRef(new Set<string>(getPref<string[]>(PREF_COLLAPSED, [])));
  const searching = useRef(false);
  const bulk = useRef(false);
  searching.current = search.trim() !== '';

  const saveCollapsed = () => setPref(PREF_COLLAPSED, [...collapsed.current]);

  const load = useCallback(async () => {
    try {
      const res = await getJson<RequestsViewResponse>('/api/views/requests');
      setRows(res.rows);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, state?.snapshot.version]);

  const counts = useMemo(() => {
    const out = {} as Record<Filter, number>;
    for (const f of FILTERS) out[f.id] = rows ? rows.filter(f.match).length : 0;
    return out;
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return [];
    // Новые запросы сверху: по дате запроса, при одной дате — по номеру (сортировка по столбцам отключена)
    const byDate = rows
      .slice()
      .sort((a, b) => (a.created === b.created ? b.no.localeCompare(a.no) : a.created < b.created ? 1 : -1));
    const byFilter = byDate.filter(FILTERS.find((f) => f.id === filter)!.match);
    const q = search.trim().toUpperCase();
    if (!q) return byFilter.map((req) => ({ req, items: req.items }));
    return byFilter
      .map((req) => {
        const matchReq = req.no.toUpperCase().includes(q);
        const items = matchReq ? req.items : req.items.filter((i) => i.art.includes(q) || i.name.toUpperCase().includes(q));
        return { req, items, matchReq };
      })
      .filter((r) => r.items.length || r.matchReq);
  }, [rows, search, filter]);

  // lang — строки запросов («от …, N позиций») пересобираются на новом языке
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo<TreeRow[]>(() => visible.map(({ req, items }) => requestRow(req, items)), [visible, lang]);

  const changeFilter = (f: Filter) => {
    setFilter(f);
    setPref(PREF_FILTER, f);
  };

  const setAll = (expand: boolean) => {
    const t = table.current;
    if (!t) return;
    bulk.current = true;
    for (const row of t.getRows()) {
      if ((row.getData() as TreeRow).kind !== 'req') continue;
      if (expand) row.treeExpand();
      else row.treeCollapse();
    }
    bulk.current = false;
    if (searching.current) return;
    if (expand) collapsed.current.clear();
    else for (const r of rows ?? []) collapsed.current.add(r.no);
    saveCollapsed();
  };

  const columns = useMemo<ColumnDefinition[]>(
    () => ([
      {
        title: t('№ запроса / OEM'),
        field: 'tree',
        width: 200,
        frozen: true,
        // Дерево запросов читается только с выравниванием влево
        hozAlign: 'left',
        headerHozAlign: 'left',
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          return row.kind === 'req' ? `<span class="t-req">${esc(cell.getValue())}</span>` : esc(cell.getValue());
        }
      },
      { title: t('OEM от закупки'), field: 'suggest', width: 130 },
      {
        title: t('Наименование'),
        field: 'name',
        width: 250,
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          return row.kind === 'req' ? `<span class="t-meta">${esc(cell.getValue())}</span>` : esc(cell.getValue());
        }
      },
      { title: t('Требуемое кол-во, шт'), field: 'qty', width: 100, formatter: numberCell, cssClass: 'col-qty' },
      { title: t('Цена для клиента, CNY'), field: 'price', width: 110, cssClass: 'col-money' },
      { title: t('Сумма для клиента, CNY'), field: 'amount', width: 120, cssClass: 'col-money' },
      { title: t('Цена фабрики, CNY'), field: 'fprice', width: 105, cssClass: 'col-money' },
      { title: t('Сумма фабрики, CNY'), field: 'famount', width: 115, cssClass: 'col-money' },
      { title: t('Маржа, CNY'), field: 'margin', width: 105, cssClass: 'col-money', formatter: moneyCell },
      { title: t('Уже закуплено, шт'), field: 'bought', width: 105, formatter: numberCell },
      { title: t('Закуплено на сумму для клиента, CNY'), field: 'boughtAmount', width: 140, cssClass: 'col-money' },
      {
        title: t('Нужно закупить, шт'),
        field: 'tobuy',
        width: 100,
        cssClass: 'col-qty',
        formatter: (cell) => {
          const value = Number(cell.getValue()) || 0;
          const row = cell.getRow().getData() as TreeRow;
          const el = cell.getElement();
          el.classList.toggle('cell-good', value <= 0);
          el.classList.toggle('cell-bad', value > 0 && !!row.overdue);
          el.classList.toggle('cell-warn', value > 0 && !row.overdue);
          return formatInt(value);
        }
      },
      {
        title: t('Наличие у фабрики, шт'),
        field: 'stock',
        width: 105,
        headerTooltip: t('Последнее проверенное наличие за вычетом закупленного после проверки'),
        formatter: (cell) => {
          const v = cell.getValue();
          return v === '' || v === undefined || v === null ? '' : formatInt(Number(v));
        }
      },
      { title: t('Дата наличия'), field: 'stockDate', width: 105 },
      {
        title: t('Излишек, шт'),
        field: 'surplus',
        width: 90,
        cssClass: 'col-qty',
        formatter: (cell) => {
          const v = cell.getValue();
          cell.getElement().classList.toggle('cell-accent', !!v);
          return v === '' || v === undefined ? '' : formatInt(Number(v));
        }
      },
      { title: t('Статус'), field: 'status', width: 165, cssClass: 'col-status', formatter: badge },
      { title: t('Автор'), field: 'author', width: 140, cssClass: 'col-note', formatter: person },
      {
        title: t('Комментарий / примечание'),
        field: 'note',
        width: 200,
        cssClass: 'col-note',
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          const v = String(cell.getValue() ?? '');
          return row.kind === 'req' ? esc(tx(v)) : esc(v);
        }
      }
    ] as ColumnDefinition[]).map((c) => ({ ...c, headerSort: false })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  if (error)
    return (
      <div className={classes.page}>
        <PageHeader title={t('Текущие запросы')} />
        <div className={classes.body}>
          <Alert color="red" variant="light" title={t('Не удалось загрузить запросы')}>
            {tx(error)}
          </Alert>
        </div>
      </div>
    );

  return (
    <div className={classes.page}>
      <PageHeader
        title={t('Текущие запросы')}
        zoom
        count={rows ? visible.length : undefined}
        subtitle={t('что заказал клиент, что закуплено и что осталось')}
        actions={
          <>
            <TextInput
              placeholder={t('Номер запроса, OEM, наименование')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftSection={<IconSearch size={15} />}
              rightSection={
                search ? (
                  <UnstyledButton onClick={() => setSearch('')} aria-label={t('Очистить поиск')} className={classes.clear}>
                    <IconX size={14} />
                  </UnstyledButton>
                ) : null
              }
              className={classes.search}
            />
            <Tooltip label={can(role, 'SPLIT_REQUEST') ? t('Закрыть по закупленному и перенести недокупленное в новый запрос') : t('Доступно отделу продаж и администратору')}>
              <Button
                variant="default"
                leftSection={<IconSplit size={15} />}
                onClick={() => setSplitOpen(true)}
                disabled={!can(role, 'SPLIT_REQUEST')}
              >
                {t('Перенести остаток')}
              </Button>
            </Tooltip>
            <Button variant="default" leftSection={<IconDownload size={15} />} onClick={() => setExportOpen(true)}>
              Excel
            </Button>
          </>
        }
      />

      <div className={classes.body}>
        <div className={classes.bar}>
          <div className={classes.filters} role="tablist" aria-label={t('Фильтр по статусу')}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={classes.chip}
                data-active={filter === f.id || undefined}
                data-kind={f.id}
                onClick={() => changeFilter(f.id)}
              >
                {t(f.title)}
                <span className={classes.chipCount}>{counts[f.id]}</span>
              </button>
            ))}
          </div>
          <div className={classes.treeBtns}>
            <Tooltip label={t('Развернуть все запросы')}>
              <UnstyledButton className={classes.ghostBtn} onClick={() => setAll(true)}>
                <IconExpand size={15} />
                <span>{t('Развернуть')}</span>
              </UnstyledButton>
            </Tooltip>
            <Tooltip label={t('Свернуть все запросы')}>
              <UnstyledButton className={classes.ghostBtn} onClick={() => setAll(false)}>
                <IconCollapse size={15} />
                <span>{t('Свернуть')}</span>
              </UnstyledButton>
            </Tooltip>
          </div>
        </div>

        <div className={classes.tableArea}>
          {!rows ? (
            <div className={classes.loading}>
              <Loader size="sm" />
              {t('Загружаю запросы…')}
            </div>
          ) : (
            <TableGrid
              gridId="requests"
              columns={columns}
              data={data}
              options={{
                dataTree: true,
                dataTreeStartExpanded: (row: RowComponent) => {
                  const d = row.getData() as TreeRow;
                  if (d.kind !== 'req') return false;
                  return searching.current || !collapsed.current.has(d.key);
                },
                dataTreeChildIndent: 22,
                dataTreeElementColumn: 'tree',
                index: 'key',
                placeholder: t('Запросов нет'),
                rowFormatter: (row) => {
                  const d = row.getData() as TreeRow;
                  row.getElement().classList.toggle('row-group', d.kind === 'req');
                  row.getElement().classList.toggle('row-cancelled', !!d.cancelled);
                }
              }}
              onReady={(instance) => {
                table.current = instance;
                const remember = (row: RowComponent, open: boolean) => {
                  if (bulk.current || searching.current) return;
                  const d = row.getData() as TreeRow;
                  if (d.kind !== 'req') return;
                  if (open) collapsed.current.delete(d.key);
                  else collapsed.current.add(d.key);
                  saveCollapsed();
                };
                instance.on('dataTreeRowExpanded', (row) => remember(row, true));
                instance.on('dataTreeRowCollapsed', (row) => remember(row, false));
              }}
            />
          )}
        </div>

      </div>
      <ExportDialog opened={exportOpen} onClose={() => setExportOpen(false)} requests={(rows ?? []).map((r) => r.no)} />
      <SplitDialog opened={splitOpen} onClose={() => setSplitOpen(false)} canCommit={can(role, 'SPLIT_REQUEST')} />
    </div>
  );
}
